// @Version: 1.0.2
import { Component, EventEmitter, Output, forwardRef, Input, OnDestroy, AfterContentInit, SimpleChanges, signal, OnChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, AbstractControl, ValidationErrors, ControlValueAccessor, Validators, FormGroup, FormBuilder } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { debounceTime } from 'rxjs/operators';
import { ErrorMessagesService } from 'src/app/shared/services/errorMessages/error-messages.service';
import { ValidatorsService } from 'src/app/shared/services/validators/validators.service';
import { IErrorCodes, IFieldItem, ITEMTYPE, VALIDATORS } from '../field-item.model';
import { FC_TEXTAREA } from './textarea.model';
import { TRANSLATE_PATH } from 'src/app/shared/shared.model';
import { Subscription } from 'rxjs';

@UntilDestroy()
@Component({
   selector: 'app-field-item-textarea',
   templateUrl: './textarea.component.html',
   providers: [
      {
         provide: NG_VALUE_ACCESSOR,
         useExisting: forwardRef(() => FieldItemTextareaComponent),
         multi: true
      },
      {
         provide: NG_VALIDATORS,
         useExisting: forwardRef(() => FieldItemTextareaComponent),
         multi: true
      },
      ErrorMessagesService
   ],
   standalone: false
})

export class FieldItemTextareaComponent implements ControlValueAccessor, Validators, OnDestroy, OnChanges  {

   public VALIDATORS = VALIDATORS; 
   public FC_TEXTAREA = FC_TEXTAREA; 

   public done$$ = signal(false);

   public fgTextarea: FormGroup = this.fb.group({});

   public hideRequireMaker: boolean = false;

   private fieldItem_: IFieldItem = {} as IFieldItem;
   private errorCodes_ = {} as IErrorCodes;
   private sub_?: Subscription;
   @Input() clearValueIfDisabled: boolean = true;

   @Input() 
   set fieldItem (value: IFieldItem) {
      this.fieldItem_ = value || {};
   }
   get fieldItem (): IFieldItem {
     return this.fieldItem_;
   }

   @Input()
   set value (val: string) {  
      this.fgTextarea.get(FC_TEXTAREA.TEXTAREA)?.setValue(val);
   }
   get value (): string {
      return this.fgTextarea.get(FC_TEXTAREA.TEXTAREA)?.value ?? '';
   }

   @Output() isRequiredMarker: EventEmitter<boolean> = new EventEmitter<boolean>();

   get errorCodes (): IErrorCodes {
      return this.errorCodes_;
   }

   constructor (private fb: FormBuilder, private sErrorMessages: ErrorMessagesService, private sValidators: ValidatorsService) { }
   
   ngOnChanges(changes: SimpleChanges): void {
      if (changes && changes.fieldItem &&  (JSON.stringify(changes.fieldItem.currentValue) != JSON.stringify(changes.fieldItem.previousValue))) {
         this.errorCodes_ = this.sErrorMessages.setErrorCodes(changes.fieldItem.currentValue.attribute_2 ?? '', TRANSLATE_PATH.FIELD_ITEM_ERROR);
         this.initFormGroup(changes.fieldItem.currentValue);
         this.onValidationChange();
         this.done$$.set(true);
      }
   }
   
   ngOnDestroy (): void {
      this.reset()
   }

   validate (control: AbstractControl): ValidationErrors | null {
      this.fgTextarea.updateValueAndValidity({emitEvent: true, onlySelf: true});
      return this.fgTextarea.get(FC_TEXTAREA.TEXTAREA) ? this.fgTextarea.get(FC_TEXTAREA.TEXTAREA)!.valid ? null : this.fgTextarea.get(FC_TEXTAREA.TEXTAREA)!.errors : null;
   }

   writeValue (value: string | undefined | null): void {
      this.fgTextarea.get(FC_TEXTAREA.TEXTAREA)?.setValue(value ? value : undefined);
      if (typeof value == 'string' && value.trim().length > 0) this.setDirtyTouched();
      this.onValidationChange();
   }

   private setDirtyTouched (): void {
      this.fgTextarea.get(FC_TEXTAREA.TEXTAREA)?.markAsDirty(); 
      this.fgTextarea.get(FC_TEXTAREA.TEXTAREA)?.markAsTouched();
   }

   onValidationChange: any = () => {}; 

   registerOnValidatorChange? (fn: () => void): void {
      this.onValidationChange = fn;
   }

   registerOnChange (fn: any): void {
      this.onChange = fn
   }
   registerOnTouched (fn: any): void { 
      this.onTouched = fn; 
   }

   private onTouched () {}

   private onChange (_: string) {}      
  
   setDisabledState (isDisabled: boolean | undefined): void { 
      if (this.fgTextarea.get(FC_TEXTAREA.TEXTAREA)) isDisabled ? this.setDisabledControls() : this.fgTextarea.get(FC_TEXTAREA.TEXTAREA)?.enable();
   }


   private reset (): void {
      this.done$$.set(false);
      this.fgTextarea = this.fb.group({});
      this.fgTextarea.markAsPristine();
      this.fieldItem_= {} as IFieldItem;
      this.errorCodes_ = {} as IErrorCodes;
      this.hideRequireMaker = false;
      this.isRequiredMarker.emit(false); 
   }

   private initFormGroup (fieldItem: IFieldItem): boolean {  
      if (this.sub_) {
         this.sub_.unsubscribe();
         this.sub_ = undefined;
      }
      this.fgTextarea =  this.fb.group({
         [FC_TEXTAREA.TEXTAREA]: this.fb.control({value: fieldItem.content, disabled: false}, {validators: this.sValidators.setValidators(fieldItem, ITEMTYPE.textarea), updateOn: 'change'})
      });

      this.sub_ = this.fgTextarea.get(FC_TEXTAREA.TEXTAREA)?.valueChanges.pipe(
         debounceTime(500)
      ).subscribe(() => {
         if (!this.value && this.fgTextarea.get(FC_TEXTAREA.TEXTAREA)?.pristine) return;
         this.onChange(this.value);
         this.onTouched();
      })

      this.fgTextarea.get(FC_TEXTAREA.TEXTAREA)?.statusChanges.subscribe(item => {
         this.onValidationChange({emitEvent: false});
      })
      

      this.hideRequireMaker = fieldItem.attribute_1 && fieldItem.attribute_1 != '' ? false : true;
      this.isRequiredMarker.emit(fieldItem.required && !this.hideRequireMaker);
      return true;
   }

   private setDisabledControls (): void {
      this.fgTextarea.get(FC_TEXTAREA.TEXTAREA)?.disable();
      if (this.clearValueIfDisabled) this.fgTextarea.get(FC_TEXTAREA.TEXTAREA)?.setValue("");
      this.fgTextarea.get(FC_TEXTAREA.TEXTAREA)?.markAsTouched();
   }
}